import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Typography } from '../common/Typography';
import { Card } from '../common/Card';
import { fetchSessionReviewGames } from '../../services/sessionReviewService';
import { format } from 'date-fns';
import { getGenderTextColor } from '../../utils/genderColors';
import { supabase } from '../../utils/supabase';
import GameContextLinks from '../games/GameContextLinks';
import { getGameContextsCount } from '../../services/gameContextsService';
import { detectGameType } from '../../utils/gameTypeUtils';

const SessionReviewGamesTable = ({ 
  sessionId, 
  simulationData = null, 
  filterPlayerIds,
  playerInteractions = null,
  gamesPlayedThisSession = null,
  clubId = null,
  hideGameLinks = false, // New prop to hide Game Links section (useful for simulations)
}) => {
  // Stabilize filter array to avoid rerunning effects when parent doesn't pass one
  const emptyFilterRef = useRef([]);
  const effectiveFilterIds = useMemo(
    () => filterPlayerIds ?? emptyFilterRef.current,
    [filterPlayerIds]
  );
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [linkCounts, setLinkCounts] = useState({});

  // Apply player filter to fetched games
  const displayedGames = useMemo(() => {
    if (!effectiveFilterIds || effectiveFilterIds.length === 0) return games;
    return games.filter(game => {
      const team1Ids = game.team_1_players?.map(p => p.id) || [];
      const team2Ids = game.team_2_players?.map(p => p.id) || [];
      const allIds = [...team1Ids, ...team2Ids];
      return effectiveFilterIds.some(id => allIds.includes(id));
    });
  }, [games, effectiveFilterIds]);

  const loadGames = useCallback(async () => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSessionReviewGames(sessionId);
      setGames(data);
    } catch (err) {
      console.error('Error loading session review games:', err);
      setError('Failed to load games data');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    // Get current user
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }
    };
    getCurrentUser();
  }, []);

  // Load link counts for displayed games
  const loadLinkCounts = useCallback(async () => {
    if (!currentUserId || displayedGames.length === 0) return;
    
    const counts = {};
    await Promise.all(
      displayedGames.map(async (game) => {
        try {
          const gameParticipantIds = [
            ...(game.team_1_players?.map(p => p.id) || []),
            ...(game.team_2_players?.map(p => p.id) || [])
          ];
          const count = await getGameContextsCount(
            game.game_id,
            currentUserId,
            clubId,
            gameParticipantIds
          );
          counts[game.game_id] = count;
        } catch (err) {
          console.error(`Error loading link count for game ${game.game_id}:`, err);
          counts[game.game_id] = 0;
        }
      })
    );
    setLinkCounts(counts);
  }, [displayedGames, currentUserId, clubId]);

  useEffect(() => {
    loadLinkCounts();
  }, [loadLinkCounts]);

  useEffect(() => {
    if (simulationData) {
      // Use simulation data directly
      let filteredGames = simulationData;
      
      // Apply player filter if provided
      if (effectiveFilterIds.length > 0) {
        filteredGames = simulationData.filter(game => {
          // Check if any filtered player is in team 1 or team 2
          const team1PlayerIds = game.team_1_players?.map(p => p.id) || [];
          const team2PlayerIds = game.team_2_players?.map(p => p.id) || [];
          const allPlayerIds = [...team1PlayerIds, ...team2PlayerIds];
          return effectiveFilterIds.some(filterId => allPlayerIds.includes(filterId));
        });
      }
      
      setGames(filteredGames);
      setLoading(false);
      setError(null);
    } else if (sessionId) {
      loadGames();
    } else {
      setLoading(false);
    }
  }, [sessionId, simulationData, effectiveFilterIds, loadGames]);

  // Calculate KPI counts from displayed games using actual player genders
  // so counts match the table (e.g. "Womens Doubles (Levels)" is counted as women's, not men's)
  const kpiCounts = useMemo(() => {
    let mensDoubles = 0;
    let womensDoubles = 0;
    let mixedDoubles = 0;
    let unbalancedGames = 0;

    const team1 = (game) => game.team_1_players || [];
    const team2 = (game) => game.team_2_players || [];

    displayedGames.forEach(game => {
      const detected = detectGameType(team1(game), team2(game));
      const id = detected?.id;

      // Classify by detected game type id (single category per game)
      if (id === 2) {
        mensDoubles++;
      } else if (id === 3) {
        womensDoubles++;
      } else if (id === 1) {
        mixedDoubles++;
      } else if (id === 6 || id === 8 || id === 9) {
        // Mens vs Mixed, Womens vs Mixed, Mens vs Womens
        unbalancedGames++;
      }
    });

    return {
      mensDoubles,
      womensDoubles,
      mixedDoubles,
      unbalancedGames
    };
  }, [displayedGames]);

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MMM dd, HH:mm');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-6 text-text-muted">
        Loading games data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6 text-danger">
        {error}
      </div>
    );
  }

  if (displayedGames.length === 0) {
    return (
      <div className="text-center py-6 text-text-muted">
        No games found for this session.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI Boxes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="default" size="small">
          <div className="p-4">
            <div className="text-sm font-medium text-text-muted mb-1">Men's Doubles</div>
            <div className="text-3xl font-bold text-text-primary">{kpiCounts.mensDoubles}</div>
          </div>
        </Card>
        <Card variant="default" size="small">
          <div className="p-4">
            <div className="text-sm font-medium text-text-muted mb-1">Women's Doubles</div>
            <div className="text-3xl font-bold text-text-primary">{kpiCounts.womensDoubles}</div>
          </div>
        </Card>
        <Card variant="default" size="small">
          <div className="p-4">
            <div className="text-sm font-medium text-text-muted mb-1">Mixed Doubles</div>
            <div className="text-3xl font-bold text-text-primary">{kpiCounts.mixedDoubles}</div>
          </div>
        </Card>
        <Card variant="default" size="small">
          <div className="p-4">
            <div className="text-sm font-medium text-text-muted mb-1">Unbalanced Games</div>
            <div className="text-3xl font-bold text-text-primary">{kpiCounts.unbalancedGames}</div>
          </div>
        </Card>
      </div>

      {/* Games: mobile cards + desktop table */}
      <div className="lg:hidden space-y-3">
        {displayedGames.map((game) => (
          <div
            key={game.game_id}
            className="rounded-lg border border-border-medium bg-surface p-4 space-y-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-text-primary">Game #{game.game_number}</div>
                <div className="text-xs text-text-muted">{formatDateTime(game.start_time)}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-text-primary">
                  {game.team_1_score} – {game.team_2_score}
                </div>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${
                    game.winning_team === 'Team 1'
                      ? 'bg-success-light text-success-dark'
                      : game.winning_team === 'Team 2'
                      ? 'bg-info-light text-info-dark'
                      : 'bg-surface-subtle text-text-secondary'
                  }`}
                >
                  {game.winning_team}
                </span>
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-1">Type</div>
              <div className="text-sm text-text-secondary">{game.game_type}</div>
              {game.pair_type && (
                <div className="text-xs text-text-muted capitalize">{game.pair_type}</div>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <div className="text-xs font-medium text-text-muted mb-1">Team 1</div>
                <div className="text-sm font-medium text-text-primary">
                  {Array.isArray(game.team_1_players) && game.team_1_players.length > 0 ? (
                    game.team_1_players.map((player, idx) => (
                      <span key={player.id || idx}>
                        <span className={getGenderTextColor(player.gender)}>{player.name}</span>
                        {player.category && (
                          <span className="ml-1 text-xs font-semibold text-text-muted">({player.category})</span>
                        )}
                        {idx < game.team_1_players.length - 1 && <span className="text-text-muted"> + </span>}
                      </span>
                    ))
                  ) : (
                    '—'
                  )}
                </div>
                {game.team_1_avg_starting_elo !== null && (
                  <div className="text-xs text-text-muted mt-0.5">ELO: {game.team_1_avg_starting_elo}</div>
                )}
              </div>
              <div>
                <div className="text-xs font-medium text-text-muted mb-1">Team 2</div>
                <div className="text-sm font-medium text-text-primary">
                  {Array.isArray(game.team_2_players) && game.team_2_players.length > 0 ? (
                    game.team_2_players.map((player, idx) => (
                      <span key={player.id || idx}>
                        <span className={getGenderTextColor(player.gender)}>{player.name}</span>
                        {player.category && (
                          <span className="ml-1 text-xs font-semibold text-text-muted">({player.category})</span>
                        )}
                        {idx < game.team_2_players.length - 1 && <span className="text-text-muted"> + </span>}
                      </span>
                    ))
                  ) : (
                    '—'
                  )}
                </div>
                {game.team_2_avg_starting_elo !== null && (
                  <div className="text-xs text-text-muted mt-0.5">ELO: {game.team_2_avg_starting_elo}</div>
                )}
              </div>
            </div>
            {game.is_altered && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-light text-warning-dark">
                Altered
              </span>
            )}
          </div>
        ))}
      </div>
    <div className="hidden lg:block overflow-x-auto">
      <table className="min-w-full divide-y divide-border-medium">
        <thead className="bg-surface-subtle">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              #
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Time
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Game Type
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Team 1
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Score
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Team 2
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Winner
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Avg ELO
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Altered
            </th>
          </tr>
        </thead>
        <tbody className="bg-surface divide-y divide-border-medium">
          {displayedGames.map((game) => (
            <tr key={game.game_id} className="hover:bg-surface-subtle">
              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-text-primary">
                {game.game_number}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-text-secondary">
                {formatDateTime(game.start_time)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-text-secondary">
                <div>{game.game_type}</div>
                {game.pair_type && (
                  <div className="text-xs text-text-muted capitalize">
                    {game.pair_type}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-text-primary">
                <div className="font-medium">
                  {Array.isArray(game.team_1_players) && game.team_1_players.length > 0 ? (
                    game.team_1_players.map((player, idx) => (
                      <span key={player.id || idx}>
                        <span className={getGenderTextColor(player.gender)}>
                          {player.name}
                        </span>
                        {player.category && (
                          <span className="ml-1 text-xs font-semibold text-text-muted">
                            ({player.category})
                          </span>
                        )}
                        {idx < game.team_1_players.length - 1 && <span className="text-text-muted"> + </span>}
                      </span>
                    ))
                  ) : (
                    '—'
                  )}
                </div>
                {game.team_1_avg_starting_elo !== null && (
                  <div className="text-xs text-text-muted">
                    ELO: {game.team_1_avg_starting_elo}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center">
                <div className="text-lg font-bold text-text-primary">
                  {game.team_1_score} - {game.team_2_score}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-text-primary">
                <div className="font-medium">
                  {Array.isArray(game.team_2_players) && game.team_2_players.length > 0 ? (
                    game.team_2_players.map((player, idx) => (
                      <span key={player.id || idx}>
                        <span className={getGenderTextColor(player.gender)}>
                          {player.name}
                        </span>
                        {player.category && (
                          <span className="ml-1 text-xs font-semibold text-text-muted">
                            ({player.category})
                          </span>
                        )}
                        {idx < game.team_2_players.length - 1 && <span className="text-text-muted"> + </span>}
                      </span>
                    ))
                  ) : (
                    '—'
                  )}
                </div>
                {game.team_2_avg_starting_elo !== null && (
                  <div className="text-xs text-text-muted">
                    ELO: {game.team_2_avg_starting_elo}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    game.winning_team === 'Team 1'
                      ? 'bg-success-light text-success-dark'
                      : game.winning_team === 'Team 2'
                      ? 'bg-info-light text-info-dark'
                      : 'bg-surface-subtle text-text-secondary'
                  }`}
                >
                  {game.winning_team}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center text-xs text-text-secondary">
                {game.team_1_avg_starting_elo !== null && game.team_2_avg_starting_elo !== null ? (
                  <div>
                    <div>{game.team_1_avg_starting_elo}</div>
                    <div className="text-text-muted">vs</div>
                    <div>{game.team_2_avg_starting_elo}</div>
                  </div>
                ) : (
                  '—'
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center">
                {game.is_altered ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-light text-warning-dark">
                    Altered
                  </span>
                ) : (
                  <span className="text-xs text-text-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* Game Context Links for each game */}
      {!hideGameLinks && displayedGames.length > 0 && currentUserId && (
        <div className="mt-6 space-y-4">
          <Typography variant="h3" className="mb-4">Game Links</Typography>
          {displayedGames.map((game) => {
            const gameParticipantIds = [
              ...(game.team_1_players?.map(p => p.id) || []),
              ...(game.team_2_players?.map(p => p.id) || [])
            ];
            const linkCount = linkCounts[game.game_id] || 0;
            return (
              <div key={`context-${game.game_id}`} className="bg-surface rounded-lg shadow border border-border-medium p-4">
                <h4 className="text-sm font-medium text-text-primary mb-2 flex items-center gap-2">
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    Links for Game #{game.game_number}
                    {linkCount > 0 && (
                      <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold text-white bg-blue-600 rounded-full min-w-[1.25rem]">
                        {linkCount}
                      </span>
                    )}
                  </span>
                </h4>
                <GameContextLinks
                  gameId={game.game_id}
                  currentUserId={currentUserId}
                  clubId={clubId}
                  gameParticipantIds={gameParticipantIds}
                  onLinksUpdated={() => {
                    // Reload link counts when links are updated
                    loadLinkCounts();
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SessionReviewGamesTable;

